import { useEffect, useState } from "react";
import { ApiError, api, type EstadoGuia, type Guia, type OrientacionPdf } from "@/lib/api";
import GuiaRichEditor from "@/components/GuiaRichEditor";
import TagInput from "@/components/TagInput";
import Modal from "@/ui/Modal";
import AyudaVista from "@/ui/AyudaVista";
import { SECCIONES_EDITOR_GUIA } from "@/components/ayuda/vistas/guias";
import { useToast } from "@/ui/Toast";

type Props = {
  open: boolean;
  onClose: () => void;
  guia?: Guia | null; // si viene, modo editar
  onSaved: (g: Guia) => void;
};

/** Modal para crear o editar una guia (know-how interno). */
export default function GuiaEditorModal({ open, onClose, guia, onSaved }: Props) {
  const toast = useToast();
  const [titulo, setTitulo] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [contenido, setContenido] = useState("");
  const [estado, setEstado] = useState<EstadoGuia>("borrador");
  const [orientacionPdf, setOrientacionPdf] = useState<OrientacionPdf>("horizontal");
  const [submitting, setSubmitting] = useState(false);

  // Cargar valores al abrir (nuevos o los de la guia a editar).
  useEffect(() => {
    if (!open) return;
    setTitulo(guia?.titulo ?? "");
    setTags(guia?.tags ?? []);
    setContenido(guia?.contenido_html ?? "");
    setEstado(guia?.estado ?? "borrador");
    setOrientacionPdf(guia?.orientacion_pdf ?? "horizontal");
  }, [open, guia]);

  async function guardar() {
    if (!titulo.trim()) {
      toast.error("Falta el título");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        titulo: titulo.trim(),
        tags,
        contenido_html: contenido,
        estado,
        orientacion_pdf: orientacionPdf,
      };
      const saved = guia
        ? await api.patch<Guia>(`/api/guias/${guia.id}`, body)
        : await api.post<Guia>("/api/guias", body);
      toast.success(guia ? "Guía actualizada" : "Guía creada");
      onSaved(saved);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude guardar la guía", msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={guia ? "Editar guía" : "Nueva guía"}
      size="xl"
      ayuda={<AyudaVista titulo="Editor de guías" manual="guias" secciones={SECCIONES_EDITOR_GUIA} />}
      footer={
        <>
          {/* Estado a la izquierda del footer (mr-auto) para que siempre
              quede visible junto a Cancelar/Guardar, sin tener que bajar
              hasta el fondo del contenido. */}
          <div className="mr-auto flex items-center gap-3">
            <label className="text-xs text-zinc-500">Estado:</label>
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="radio"
                checked={estado === "borrador"}
                onChange={() => setEstado("borrador")}
              />
              Borrador
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="radio"
                checked={estado === "publicado"}
                onChange={() => setEstado("publicado")}
              />
              Publicado
            </label>
          </div>
          <button onClick={onClose} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={guardar}
            disabled={submitting}
            className="btn-primary disabled:opacity-50"
          >
            {submitting ? "Guardando..." : "Guardar"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Título</label>
          <input
            className="input w-full"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Cómo se hace..."
          />
        </div>

        <div>
          <label className="text-xs text-zinc-500 block mb-1">
            Etiquetas
            <span className="ml-2 text-zinc-600 normal-case">
              Enter o coma para agregar; click en × para quitar.
            </span>
          </label>
          <TagInput
            value={tags}
            onChange={setTags}
            placeholder="ej. configuración, queclink, firmware…"
            suggestionsUrl="/api/guias/tags"
          />
        </div>

        <div>
          <label className="text-xs text-zinc-500 block mb-1">Contenido</label>
          <GuiaRichEditor
            value={contenido}
            onChange={setContenido}
            orientacionPdf={orientacionPdf}
            onOrientacionPdf={setOrientacionPdf}
            placeholder="Explica el procedimiento. Pega imágenes y videos de YouTube con los botones de la barra."
            minHeight={300}
          />
        </div>

      </div>
    </Modal>
  );
}
